import React from 'react';
import { useFormContext } from 'react-hook-form';
import Editor from '@monaco-editor/react';
import {
  FormField,
  FormItem,
  FormLabel,
  FormDescription,
  FormMessage,
  FormControl,
} from '@/shared/components/ui/form';
import { useTheme } from '@/shared/hooks/use-theme';
import { BaseFieldProps } from './types';

/**
 * Normaliza o valor pra string segura de exibir no Monaco. Sem isso, objetos
 * preenchidos viram "[object Object]" via coerção (`String(obj)`).
 *   - null/undefined → ''
 *   - objeto vazio   → ''
 *   - objeto preenchido → JSON.stringify formatado
 *   - demais         → String()
 */
function formatCodeValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') {
    return Object.keys(value as object).length === 0
      ? ''
      : JSON.stringify(value, null, 2);
  }
  return String(value);
}

export const CodeEditorField: React.FC<BaseFieldProps> = ({
  field,
  name,
  control,
}) => {
  const { setValue } = useFormContext();
  const { theme } = useTheme();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const language = ((field as any).language as string) || 'javascript';

  return (
    <FormField
      control={control}
      name={name}
      render={({ field: formField }) => {
        const value = formatCodeValue(formField.value);

        return (
          <FormItem>
            <FormLabel>{field.label}</FormLabel>
            <FormControl>
              <div className="rounded border overflow-hidden">
                <Editor
                  height="320px"
                  language={language}
                  value={value}
                  onChange={(val) => {
                    const v = val ?? '';
                    formField.onChange(v);
                    setValue(name, v);
                  }}
                  theme={theme === 'dark' ? 'vs-dark' : 'vs'}
                  options={{
                    minimap: { enabled: false },
                    wordWrap: 'on',
                    fontSize: 13,
                    lineHeight: 20,
                    scrollBeyondLastLine: false,
                    automaticLayout: true,
                  }}
                />
              </div>
            </FormControl>
            {field.description && <FormDescription>{field.description}</FormDescription>}
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
};

export default CodeEditorField;
